import cn from 'classnames'

import { Translation } from 'cng-common/features/translations'

import { Skeleton } from '../skeleton'

import { SECTION_LOADING_TRANSLATION_KEYS } from './translations'

import styles from './SectionLoading.module.scss'

/* The outline a section is drawn on: a toolbar, a title and rows of label and value. */
const TOOLBAR = ['120px', '220px', '130px']
const ROWS = 18

interface ISectionLoadingProps {
  /** Shown or lifted; it stays in place and fades so the content under it appears smoothly. */
  show: boolean
  /** The section's name, already translated, said under the ring. */
  name?: string
  className?: string
}

/**
 * One loading state for every section: the section's still outline under a frosted layer, with a
 * thin ring and what is loading on top. It covers the content while it loads, so the section's own
 * spinners and half-drawn parts stay out of sight, and fades away once the content is ready.
 *
 * Use it over any area that loads in steps — a section window, a card, a panel — with
 * `useContentLoading` saying when the area is ready.
 */
export const SectionLoading = ({ show, name, className }: ISectionLoadingProps) => (
  <div
    className={cn(styles.root, { [styles.hidden]: !show }, className)}
    role="status"
    aria-live="polite"
    aria-hidden={!show}
  >
    <div className={styles.outline} aria-hidden>
      <div className={styles.toolbar}>
        {TOOLBAR.map((width) => (
          <Skeleton key={width} className={styles.block} width={width} height={30} />
        ))}
      </div>
      <Skeleton className={cn(styles.block, styles.title)} width="180px" height={18} />
      {Array.from({ length: ROWS }, (_, index) => (
        <div key={index} className={styles.row}>
          <Skeleton className={styles.block} width="140px" height={14} />
          <Skeleton className={styles.block} height={14} />
        </div>
      ))}
    </div>

    <div className={styles.frost} />

    <div className={styles.message}>
      <span className={styles.ring} aria-hidden />
      <span className={styles.caption}>
        {name ? (
          <Translation
            span
            text={SECTION_LOADING_TRANSLATION_KEYS.named}
            replaceParams={[{ key: '{name}', value: name }]}
          />
        ) : (
          <Translation span text={SECTION_LOADING_TRANSLATION_KEYS.plain} />
        )}
      </span>
    </div>
  </div>
)

SectionLoading.displayName = 'SectionLoading'
